import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, Check, ArrowDownToLine, ExternalLink } from "lucide-react";
import { Reveal, FadeIn } from "@/components/motion/Reveal";
import { CONTACT_EMAIL } from "@/data/content";
import { OUTREACH_TEMPLATES, SIGNATURE } from "@/data/outreach";

const PDF_PATH = "/phaedra-paulson-stylist-portfolio.pdf";

export default function ResourceKit() {
  const [copied, setCopied] = useState(null);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const portfolioUrl = `${origin}${PDF_PATH}`;
  const siteUrl = origin;

  useEffect(() => {
    document.title = "Outreach Kit — Private";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  const fill = (s) =>
    s.replaceAll("{{Portfolio Link}}", portfolioUrl).replaceAll("{{Site Link}}", siteUrl);

  const templates = useMemo(
    () =>
      OUTREACH_TEMPLATES.map((t) => ({
        ...t,
        subjectFilled: fill(t.subject),
        fullText: `${fill(t.body)}\n\n${fill(SIGNATURE)}`,
      })),
    [portfolioUrl, siteUrl]
  );

  const copy = async (text, key) => {
    const done = () => {
      setCopied(key);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 1600);
    };
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return done();
      }
      throw new Error("no clipboard api");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        return done();
      } catch {
        toast.error("Couldn't copy — select the text and copy manually");
      }
    }
  };

  return (
    <section className="px-6 pb-32 pt-36 md:px-12 md:pt-48" data-testid="resource-kit-page">
      <div className="mx-auto max-w-[1100px]">
        <FadeIn>
          <p className="overline mb-6 flex items-center gap-3 text-muted">
            <span className="h-px w-10 bg-clay" />
            Private — Outreach Kit
          </p>
        </FadeIn>
        <Reveal>
          <h1 className="font-display text-[13vw] font-extralight leading-[0.9] tracking-tight text-ink md:text-[6vw]">
            Studio outreach
          </h1>
          <p className="mt-6 max-w-xl font-body text-sm leading-relaxed text-stone">
            Your working kit for reaching NYC studios as a freelance on-figure & still-life
            stylist. Personalize <span className="text-ink">{"{{First Name}}"}</span> and{" "}
            <span className="text-ink">{"{{Studio}}"}</span> per contact (use the “Suggested
            Approach” note in your CRM), then paste into Gmail. Links auto-fill to this site.
          </p>
          <p className="mt-2 font-body text-xs text-muted">
            This page is unlisted and set to no-index. Keep the URL private.
          </p>
        </Reveal>

        {/* Portfolio + quick links */}
        <Reveal>
          <div className="mt-12 grid gap-4 border-y border-hairline py-8 sm:grid-cols-2">
            <div>
              <p className="overline mb-3 text-muted">Portfolio</p>
              <div className="flex flex-wrap items-center gap-3">
                <a
                  href={PDF_PATH}
                  download
                  data-testid="kit-portfolio-download"
                  className="group inline-flex items-center gap-2 rounded-full border border-ink px-5 py-2.5 font-body text-sm text-ink transition-colors duration-300 hover:bg-ink hover:text-paper"
                >
                  Download PDF
                  <ArrowDownToLine className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                </a>
                <button
                  type="button"
                  data-testid="kit-copy-portfolio-link"
                  onClick={() => copy(portfolioUrl, "pdf")}
                  className="inline-flex items-center gap-2 font-body text-sm text-stone transition-colors hover:text-ink"
                >
                  {copied === "pdf" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  Copy link
                </button>
              </div>
            </div>
            <div>
              <p className="overline mb-3 text-muted">Quick reference</p>
              <ul className="space-y-1.5 font-body text-sm text-ink">
                <li className="flex items-center gap-2">
                  <button onClick={() => copy(CONTACT_EMAIL, "email")} className="link-underline" data-testid="kit-copy-email">
                    {CONTACT_EMAIL}
                  </button>
                </li>
                <li>
                  <a href={siteUrl} target="_blank" rel="noreferrer" className="link-underline inline-flex items-center gap-1.5">
                    {siteUrl.replace(/^https?:\/\//, "")} <ExternalLink className="h-3 w-3" />
                  </a>
                </li>
                <li className="text-stone">@mkingsleystudio · New York City</li>
              </ul>
            </div>
          </div>
        </Reveal>

        {/* Templates */}
        <div className="mt-14 space-y-10">
          {templates.map((t, i) => (
            <Reveal key={t.id} delay={i * 0.05}>
              <article
                data-testid={`template-${t.id}`}
                className="rounded-xl border border-hairline bg-paper2 p-6 md:p-8"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="max-w-xl">
                    <p className="overline text-clay">Template {i + 1}</p>
                    <h2 className="mt-2 font-display text-2xl font-light tracking-tight text-ink">
                      {t.name}
                    </h2>
                    <p className="mt-2 font-body text-xs leading-relaxed text-muted">{t.when}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-testid={`copy-subject-${t.id}`}
                      onClick={() => copy(t.subjectFilled, `subj-${t.id}`)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-hairline px-3.5 py-1.5 font-body text-xs text-stone transition-colors hover:border-ink hover:text-ink"
                    >
                      {copied === `subj-${t.id}` ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      Subject
                    </button>
                    <button
                      type="button"
                      data-testid={`copy-body-${t.id}`}
                      onClick={() => copy(t.fullText, `body-${t.id}`)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-ink bg-ink px-3.5 py-1.5 font-body text-xs text-paper transition-opacity hover:opacity-85"
                    >
                      {copied === `body-${t.id}` ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      Copy email
                    </button>
                  </div>
                </div>

                <div className="mt-6 border-t border-hairline pt-5">
                  <p className="overline mb-1 text-muted">Subject</p>
                  <p className="font-body text-sm text-ink">{t.subjectFilled}</p>
                </div>
                <div className="mt-4">
                  <p className="overline mb-2 text-muted">Body</p>
                  <pre className="whitespace-pre-wrap font-body text-sm leading-relaxed text-ink/90">
{t.fullText}
                  </pre>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
